import { createMigrate, createTransform } from "redux-persist";
import type { MigrationManifest, PersistedState, PersistMigrate, Transform } from "redux-persist";

type PersistedRecord = Record<string, unknown>;

type KeyRenames = Readonly<Record<string, string>>;

export const PERSIST_VERSION: number = 1;

const SELECTION_FILTER_SLICE: string = "selecaoFilter";
const DRILL_FILTER_SLICE: string = "drillFilter";
const SELECTION_FILTERS_FIELD: string = "filters";
const DRILL_ENTRIES_FIELD: string = "porRemarcacao";

const SELECTION_FILTER_RENAMES: KeyRenames = {
  familia: "family",
};

const DRILL_GROUP_RENAMES: Readonly<Record<string, KeyRenames>> = {
  simpleFilters: {
    matriz: "matrix",
    statusEscoamento: "stCategory",
    bucketIdade: "ageBucket",
    familiaOrigem: "sourceFamily",
    departamento: "department",
    grupo: "group",
    subgrupo: "subgroup",
    codigo: "code",
  },
  checks: {
    filtroSazonal: "seasonalFilter",
    filtroKvi: "kviFilter",
    filtroCarteira: "backlogFilter",
    filtroRemarcados: "repricedFilter",
    filtroSituacao: "itemStatusFilter",
    soMargemNegativa: "negativeMarginOnly",
    soRemarcados: "markedOnly",
    soSugeridos: "suggestedOnly",
  },
  advancedFilters: {
    descricao: "description",
    idadeDe: "ageFrom",
    idadeAte: "ageTo",
    stMenorQue: "stLessThan",
    estoqueDe: "stockFrom",
    estoqueAte: "stockTo",
    ultimaRemarcacaoDe: "lastRepricingFrom",
    ultimaRemarcacaoAte: "lastRepricingTo",
  },
};

const isRecord = (value: unknown): value is PersistedRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

type KeysTransform = (source: PersistedRecord, renames: KeyRenames) => PersistedRecord;

const copyToEnglishKeys: KeysTransform = (source: PersistedRecord, renames: KeyRenames): PersistedRecord => {
  const copies: [string, unknown][] = Object.entries(renames)
    .filter(([legacy]: [string, string]): boolean => Object.hasOwn(source, legacy))
    .map(([legacy, english]: [string, string]): [string, unknown] => [english, source[legacy]]);
  return { ...source, ...Object.fromEntries(copies) };
};

const mirrorToLegacyKeys: KeysTransform = (source: PersistedRecord, renames: KeyRenames): PersistedRecord => {
  const withoutLegacy: [string, unknown][] = Object.entries(source).filter(
    ([key]: [string, unknown]): boolean => !Object.hasOwn(renames, key),
  );
  const mirrors: [string, unknown][] = Object.entries(renames)
    .filter(([, english]: [string, string]): boolean => Object.hasOwn(source, english))
    .map(([legacy, english]: [string, string]): [string, unknown] => [legacy, source[english]]);
  return Object.fromEntries([...withoutLegacy, ...mirrors]);
};

const dropMirroredLegacyKeys: KeysTransform = (source: PersistedRecord, renames: KeyRenames): PersistedRecord =>
  Object.fromEntries(
    Object.entries(source).filter(([key]: [string, unknown]): boolean => {
      const english: string | undefined = renames[key];
      return english === undefined || !Object.hasOwn(source, english);
    }),
  );

const mapField = (
  source: PersistedRecord,
  field: string,
  transform: (value: PersistedRecord) => PersistedRecord,
): PersistedRecord => {
  const value: unknown = source[field];
  if (!isRecord(value)) return source;
  return { ...source, [field]: transform(value) };
};

const transformDrillEntry = (entry: PersistedRecord, keys: KeysTransform): PersistedRecord =>
  Object.entries(DRILL_GROUP_RENAMES).reduce<PersistedRecord>(
    (migrated: PersistedRecord, [group, renames]: [string, KeyRenames]): PersistedRecord =>
      mapField(migrated, group, (filters: PersistedRecord): PersistedRecord => keys(filters, renames)),
    entry,
  );

const transformDrillEntries = (entries: PersistedRecord, keys: KeysTransform): PersistedRecord =>
  Object.fromEntries(
    Object.entries(entries).map(([markdownKey, entry]: [string, unknown]): [string, unknown] => {
      if (!isRecord(entry)) return [markdownKey, entry];
      return [markdownKey, transformDrillEntry(entry, keys)];
    }),
  );

const transformSelectionSlice = (slice: PersistedRecord, keys: KeysTransform): PersistedRecord =>
  mapField(slice, SELECTION_FILTERS_FIELD, (filters: PersistedRecord): PersistedRecord =>
    keys(filters, SELECTION_FILTER_RENAMES),
  );

const transformDrillSlice = (slice: PersistedRecord, keys: KeysTransform): PersistedRecord =>
  mapField(slice, DRILL_ENTRIES_FIELD, (entries: PersistedRecord): PersistedRecord =>
    transformDrillEntries(entries, keys),
  );

const SLICE_TRANSFORMS: Readonly<Record<string, (slice: PersistedRecord, keys: KeysTransform) => PersistedRecord>> = {
  [SELECTION_FILTER_SLICE]: transformSelectionSlice,
  [DRILL_FILTER_SLICE]: transformDrillSlice,
};

const transformSlice = (slice: unknown, sliceKey: string | number | symbol, keys: KeysTransform): unknown => {
  const transform: ((slice: PersistedRecord, keys: KeysTransform) => PersistedRecord) | undefined =
    SLICE_TRANSFORMS[String(sliceKey)];
  if (!transform || !isRecord(slice)) return slice;
  return transform(slice, keys);
};

const migrateFiltersToEnglishContract = (state: PersistedState): PersistedState => {
  if (!isRecord(state)) return state;
  return Object.fromEntries(
    Object.entries(state).map(([sliceKey, slice]: [string, unknown]): [string, unknown] => [
      sliceKey,
      transformSlice(slice, sliceKey, copyToEnglishKeys),
    ]),
  ) as PersistedState;
};

const persistMigrations: MigrationManifest = {
  1: migrateFiltersToEnglishContract,
};

export const persistMigrate: PersistMigrate = createMigrate(persistMigrations, {
  debug: false,
});

export const legacyFilterKeysTransform: Transform<unknown, unknown> = createTransform<unknown, unknown>(
  (inboundState: unknown, sliceKey: string | number | symbol): unknown =>
    transformSlice(inboundState, sliceKey, mirrorToLegacyKeys),
  (outboundState: unknown, sliceKey: string | number | symbol): unknown =>
    transformSlice(outboundState, sliceKey, dropMirroredLegacyKeys),
  { whitelist: [SELECTION_FILTER_SLICE, DRILL_FILTER_SLICE] },
);
